import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { PasswordInput } from "@/components/PasswordInput";
import { PasswordStrength, isPasswordStrong } from "@/components/PasswordStrength";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const navigate = useNavigate();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) {
      toast.error("Passwords don't match");
      return;
    }
    if (!isPasswordStrong(pw)) {
      toast.error("Password must be at least 8 characters with an uppercase letter, a lowercase letter, and a number.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password: pw });
      setDone(true);
      toast.success("Password reset — you can now sign in");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <div className="container-x py-20 max-w-md" data-testid="reset-password-page">
        <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold">Invalid reset link</h1>
        <p className="text-[#5C6B64] mt-2">This link is missing or invalid. <Link to="/recover" className="text-[#CC5A3A] font-medium">Request a new one</Link>.</p>
      </div>
    );
  }

  return (
    <div className="container-x py-20 max-w-md" data-testid="reset-password-page">
      <h1 className="font-heading text-3xl text-[#1E3F33] font-semibold">Set a new password</h1>
      {done ? (
        <p className="text-[#2D6A4F] mt-4" data-testid="reset-done">Your password has been reset. Redirecting to sign in…</p>
      ) : (
        <form onSubmit={submit} className="mt-8 space-y-5">
          <div>
            <label className="label-soft">New password</label>
            <PasswordInput testId="reset-password-input" toggleTestId="reset-password-toggle" required minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} autoComplete="new-password" />
            <PasswordStrength password={pw} testId="reset-password-strength" />
          </div>
          <div>
            <label className="label-soft">Confirm new password</label>
            <PasswordInput testId="reset-password-confirm" toggleTestId="reset-password-confirm-toggle" required minLength={6} value={confirm} onChange={(e) => setConfirm(e.target.value)} autoComplete="new-password" />
          </div>
          <button data-testid="reset-password-submit" disabled={busy} className="btn-primary w-full">{busy ? "Resetting…" : "Reset password"}</button>
        </form>
      )}
    </div>
  );
}
